import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AuctionMap from '../../components/AuctionMap';

// Headless-only print route — Puppeteer navigates here directly (see
// backend/services/contractBuilder.js's captureMapSnapshot) using a
// short-lived signed token instead of a normal login session, so this
// deliberately bypasses the app's PrivateRoute/AdminRoute auth entirely (see
// App.js). Renders just the map with no Navbar/Sidebar/app chrome, and
// signals completion via a `[data-map-ready="true"]` marker element once
// AuctionMap's auto-zoom settles, so Puppeteer knows exactly when to
// screenshot `[data-map-container]`.
const BidMapSnapshot = () => {
  const { bidId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [bidData, setBidData] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    axios.get(`${apiBase}/contracts/print/bid-map/${bidId}`, { params: { token } })
      .then((res) => setBidData(res.data))
      .catch((err) => setError(err.response?.data?.msg || 'Failed to load bid map data'));
  }, [bidId, token]);

  const handleReady = useCallback(() => setReady(true), []);
  // handleReady's own setReady(true) call re-renders this component — a plain
  // `[bidData]` literal here would be a brand-new array on that re-render,
  // re-triggering AuctionMap's bids-dependent auto-zoom effect a second
  // (harmless but wasteful, and confusing in the [MapSnapshot] server log)
  // time for data that hasn't actually changed.
  const bids = useMemo(() => (bidData ? [bidData] : []), [bidData]);

  if (error) return <div>{error}</div>;
  if (!bidData) return <div />;

  return (
    <div style={{ width: '100vw', height: '100vh', margin: 0, padding: 0 }}>
      <div data-map-container="true" style={{ width: '100%', height: '100%' }}>
        <AuctionMap bids={bids} onReady={handleReady} instantZoom />
      </div>
      {ready && <div data-map-ready="true" style={{ display: 'none' }} />}
    </div>
  );
};

export default BidMapSnapshot;
